import React, { useEffect, useState, useMemo } from 'react';
import {DashboardHero, DashboardMetrics} from '../dashboardui';
import '../operationsui.css';
import { Alert, Badge, Button, Card, Col, Form, Modal, Nav, Row, Table, Spinner, Pagination } from 'react-bootstrap';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPrint, faRotateLeft, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useStockTransfers } from '../../contextjs/StockTransferContext';
import { useUser } from '../../UserContext';
import {
    apiInstance,
    fetchAllProductsMinimalWithUnits,
    fetchCurrentStore,
    fetchStores,
    fetchTransferRequestSourceStock,
} from '../../api/api';

const initialItem = { product: "", quantity: "", unit: "", conversion_factor: 1, availableUnits: [],
    sourceStock: null, sourceStockUnit: "", sourceStockLoading: false, sourceStockError: "",
};

export default function StockTransferPage() {
    const { transfers, requests, transferCount, requestCount, loading, loadTransfers, loadRequests, addTransfer, addRequest,
        editRequest, approveRequest, rejectRequestAction, returnTransferItemsAction, printTransferReceiptAction} = useStockTransfers();
    const { user } = useUser();

    const navigate = useNavigate();

    const [stores, setStores] = useState([]);
    const [currentStore, setCurrentStore] = useState(null);
    const [allProducts, setAllProducts] = useState([]);

    const [activeTab, setActiveTab] = useState('requests');
    const [transferPage, setTransferPage] = useState(1);
    const [requestPage, setRequestPage] = useState(1);
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [receiving, setReceiving] = useState(null);
    const [receiptIssue, setReceiptIssue] = useState('');
    const [receiptConfirmed, setReceiptConfirmed] = useState(false);
    const [receiptBusy, setReceiptBusy] = useState(false);
    const [receiptError, setReceiptError] = useState('');
    const [viewModalType, setViewModalType] = useState(null); // 'request' | 'transfer'
    const [viewRecord, setViewRecord] = useState(null);
    const [returnTransferRecord, setReturnTransferRecord] = useState(null);
    const [returnItems, setReturnItems] = useState([])
    const [items, setItems] = useState([{...initialItem}]);
    const [requestForm, setRequestForm] = useState({
        requesting_store: "", source_store: "", remarks: "",
    });
    const [transferForm, setTransferForm] = useState({
        from_store: "", to_store: "", remarks: "",
    });
    const [stockPreview, setStockPreview] = useState([]);
    const [editingRequestId, setEditingRequestId] = useState(null);
    const [transferFilters, setTransferFilters] = useState({
        start_date: "",
        end_date: "",
        transfer_id: "",
        product: "",
        status: "",
    });

    const [message, setMessage] = useState(null);
    const [transferError, setTransferError] = useState('');
    const isAdmin = Boolean(
        user?.is_superuser ||
        user?.is_staff ||
        user?.groups?.includes?.('Admin') ||
        user?.groups?.includes?.('Administrator')
    );
    const isWarehouse = currentStore?.code === 'WH';
    const canReturn = Boolean(user?.is_superuser || user?.groups?.some?.(g => ['Admin', 'Administrator', 'Manager'].includes(g)));
    const openReceive = transfer => { setReceiving(transfer); setReceiptIssue(''); setReceiptConfirmed(false); setReceiptError(''); };
    const confirmReceipt = async () => {
        if (receiptBusy || !receiptConfirmed) return;
        setReceiptBusy(true); setReceiptError('');
        try {
            await apiInstance.post(`/stock-transfers/${receiving.id}/receive/`, {confirm_full_receipt:true, issue:receiptIssue});
            setReceiving(null);
            setMessage({type:'success', text:'Full transfer received. Any reported issue has been saved and sent to the dispatcher and active admin chat accounts.'});
            await loadTransfers(transferFilters, transferPage);
        } catch (error) { setReceiptError(error.response?.data?.detail || 'Unable to confirm receipt. Refresh the transfer list before retrying if the connection was interrupted.'); }
        finally { setReceiptBusy(false); }
    };

    const handleBack = () => {
        navigate(-1);
    };

    const warehouseStore = useMemo(
        () => (Array.isArray(stores) ? stores : []).find((s) => s?.code === 'WH') || null,
        [stores]
    );

    const storeNameById = useMemo(() => {
        const map = new Map();
        (Array.isArray(stores) ? stores : []).forEach((s) => {
            if (s?.id != null) map.set(String(s.id), s.name);
        });
        return map;
    }, [stores]);

    const productById = useMemo(() => {
        const map = new Map();
        (Array.isArray(allProducts) ? allProducts : []).forEach((p) => {
            if (p?.id != null) map.set(String(p.id), p);
        });
        return map;
    }, [allProducts]);

    const getStoreName = (storeId) => {
        if (storeId == null || storeId === '') return '-';
        return storeNameById.get(String(storeId)) || String(storeId);
    };

    const getProductName = (productId) => {
        if (productId == null || productId === '') return '-';
        const p = productById.get(String(productId));
        return p?.product_name || String(productId);
    };

    const getUnitLabel = (productId, unitId) => {
        if (!unitId) return '-';

        const product = productById.get(String(productId));
        if (!product) return String(unitId);

        // check base unit
        if (product.base_unit && String(product.base_unit.id) === String(unitId)) {
            return product.base_unit.name; //full name
        }

        // check alternative units
        const altUnit = (product.units || []).find((u) => String(u.unit?.id) === String(unitId));

        if (altUnit?.unit) {
            return altUnit.unit.name;
        }

        return String(unitId);
    };

    const formatDateTime = (value) => {
        if (!value) return '-';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return date.toLocaleString();
    };

    const openViewModal = async (type, record) => {
        setViewModalType(type);
        setViewRecord(record || null);

        if (type === 'request') {
            try {
                const res = await apiInstance.get(`/stock-transfer-requests/${record.id}/stock_preview/`);
                setStockPreview(res.data);
            } catch (err) {
                console.error('Error fetching stock preview:', err);
                setStockPreview([]);
            }
        } else {
            setStockPreview([]);
        }
        setShowViewModal(true);
    };

    useEffect(() => {
        loadTransfers({}, 1);
        loadRequests(1);
    }, [loadTransfers, loadRequests]);

    useEffect(() => {
        let cancelled = false;

        const loadStoreContext = async () => {
            try {
                const [storesData, currentStoreData] = await Promise.all([
                    fetchStores(),
                    fetchCurrentStore(),
                ]);
                if (cancelled) return;

                setStores(Array.isArray(storesData) ? storesData : (storesData?.results || []));
                setCurrentStore(currentStoreData || null);
            } catch (error) {
                console.error('Error loading stores/current store:', error);
                if (cancelled) return;
                setStores([]);
                setCurrentStore(null);
            }
        };

        loadStoreContext();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const load = async () => {
            const data = await fetchAllProductsMinimalWithUnits();
            setAllProducts(Array.isArray(data) ? data : (data?.results || []));
        };
        load();
    }, []);

    useEffect(() => {
        if (currentStore?.id) {
            setRequestForm((prev) => ({...prev, requesting_store: currentStore.id }));
            setTransferForm((prev) => ({...prev, from_store: currentStore.id }));
        }
    }, [currentStore]);

    useEffect(() => {
        // For non-warehouse stores, requests must originate from the current store and be sent to the warehouse.
        if (!isWarehouse && currentStore?.id && warehouseStore?.id) {
            setRequestForm((prev) => ({
                ...prev,
                requesting_store: currentStore.id,
                source_store: warehouseStore.id,
            }));
        }
    }, [isWarehouse, currentStore, warehouseStore]);

    const handleApprove = async (requestId) => {
        setMessage(null);
        try {
            const response = await approveRequest(requestId);
            setRequestPage(1);
            setTransferPage(1);
            const printNote = response?.print_warning ? ` Receipt print warning: ${response.print_warning}` : '';
            setMessage({ type: 'success', text: `Request approved successfully.${printNote}` });
        } catch (error) {
            setMessage({
                type: 'danger',
                text: error?.response?.data?.detail || 'Failed to approve request.',
            });
        }
    };

    const openReturnModal = (transfer) => {
        setReturnTransferRecord(transfer);
        setReturnItems((transfer.items || []).map((item) => ({
            item: item.id,
            quantity: "",
        })));
        setShowReturnModal(true);
    };

    const closeReturnModal = () => {
        setShowReturnModal(false);
        setReturnTransferRecord(null);
        setReturnItems([]);
    };

    const getReturnableQuantity = (item) => {
        const quantity = Number(item.quantity || 0);
        const returned = Number(item.returned_quantity || 0);
        return Math.max(0, quantity - returned);
    };

    const handleReturnQuantityChange = (itemId, quantity) => {
        setReturnItems((prev) => prev.map((row) => (
            String(row.item) === String(itemId) ? {...row, quantity} : row
        )));
    };

    const fillAllReturnQuantities = () => {
        setReturnItems((returnTransferRecord?.items || []).map((item) => ({
            item: item.id,
            quantity: getReturnableQuantity(item),
        })));
    };

    const submitReturnItems = async () => {
        const transferLabel = returnTransferRecord?.transfer_id || `TR-${returnTransferRecord?.id}`;
        const payloadItems = returnItems
            .map((row) => ({...row, quantity: Number(row.quantity || 0)}))
            .filter((row) => row.quantity > 0);

        if (!payloadItems.length) {
            setMessage({type: 'danger', text: 'Enter at least one quantity to return.'});
            return;
        }

        setMessage(null);
        try {
            await returnTransferItemsAction(returnTransferRecord.id, payloadItems);
            setTransferPage(1);
            setMessage({ type: 'success', text: `Transfer ${transferLabel} returned successfully.` });
            closeReturnModal();
        } catch (error) {
            console.error('Error returning transfer items:', error);
            setMessage({
                type: 'danger',
                text: error?.response?.data?.detail || `Failed to return transfer ${transferLabel}.`,
            });
        }
    };

    const handleReject = async (requestId) => {
        setMessage(null);
        try {
            await rejectRequestAction(requestId);
            setRequestPage(1);
            setMessage({ type: 'success', text: `Request #${requestId} rejected.` });
        } catch (error) {
            setMessage({
                type: 'danger',
                text: error?.response?.data?.detail || 'Failed to reject request.',
            });
        }
    };

    const handlePrintTransfer = async (transfer) => {
        const transferLabel = transfer?.transfer_id || `TR-${transfer?.id}`;
        setMessage(null);
        try {
            const response = await printTransferReceiptAction(transfer.id);
            setMessage({
                type: 'success',
                text: response?.message || `Transfer ${transferLabel} receipt sent to printer.`,
            });
        } catch (error) {
            console.error('Error printing transfer receipt:', error);
            setMessage({
                type: 'danger',
                text: error?.response?.data?.detail || `Failed to print transfer ${transferLabel}.`,
            });
        }
    };

    const handleItemChange = (index, field, value) => {
        const updated = [...items];
        updated[index][field] = value;
        setItems(updated);
    };

    const buildAvailableUnits = (product) => {
        const availableUnits = [];

        // base unit first (conversion = 1)
        if (product?.base_unit?.id) {
            availableUnits.push({
                id: product.base_unit.id,
                name: product.base_unit.name,
                abbreviation: product.base_unit.abbreviation,
                conversion_factor: 1,
                is_base: true,
            });
        }

        // alternative units
        if (Array.isArray(product?.units) && product.units.length) {
            product.units.forEach((u) => {
                if (!u?.unit?.id) return;
                availableUnits.push({
                    id: u.unit.id,
                    name: u.unit.name,
                    abbreviation: u.unit.abbreviation,
                    conversion_factor: u.conversion_factor,
                    is_base: false,
                });
            });
        }

        return availableUnits;
    };

    const handleProductSelect = async (index, productId) => {
        const updated = [...items];

        updated[index].product = productId;
        updated[index].sourceStock = null;
        updated[index].sourceStockUnit = '';
        updated[index].sourceStockLoading = Boolean(
            showRequestModal && requestForm.source_store && productId
        );
        updated[index].sourceStockError = '';

        const product = (Array.isArray(allProducts) ? allProducts : []).find(
            (p) => String(p.id) === String(productId)
        );

        const availableUnits = buildAvailableUnits(product);
        updated[index].availableUnits = availableUnits;

        // default to base unit (or the only available unit)
        const defaultUnit = availableUnits[0] || null;
        updated[index].unit = defaultUnit ? defaultUnit.id : '';
        updated[index].conversion_factor = defaultUnit ? (defaultUnit.conversion_factor || 1) : 1;

        setItems(updated);

        if (!showRequestModal || !requestForm.source_store || !productId) return;

        try {
            const stock = await fetchTransferRequestSourceStock(requestForm.source_store, productId);
            setItems((current) => current.map((item, itemIndex) => (
                itemIndex === index && String(item.product) === String(productId)
                    ? {
                        ...item,
                        sourceStock: stock.available_qty,
                        sourceStockUnit: stock.base_unit_name || 'base units',
                        sourceStockLoading: false,
                        sourceStockError: '',
                    }
                    : item
            )));
        } catch (error) {
            setItems((current) => current.map((item, itemIndex) => (
                itemIndex === index && String(item.product) === String(productId)
                    ? {
                        ...item,
                        sourceStock: null,
                        sourceStockLoading: false,
                        sourceStockError: error?.response?.data?.detail || 'Unable to load source stock.',
                    }
                    : item
            )));
        }
    };

    const handleUnitSelect = (index, unitId) => {
        const updated = [...items];
        updated[index].unit = unitId;

        const selectedUnit = (updated[index].availableUnits || []).find(
            (u) => String(u.id) === String(unitId)
        );
        updated[index].conversion_factor = selectedUnit ? (selectedUnit.conversion_factor || 1) : 1;
        setItems(updated);
    };

    const addLine = () => setItems([...items, {...initialItem}]);
    const removeLine = (index) => setItems(items.filter((_, i) => i !== index));

    const resetItems = () => setItems([{...initialItem}]);

    const closeRequestModal = () => {
        setShowRequestModal(false);
        setEditingRequestId(null);
        resetItems();
    };

    const submitRequest = async (e) => {
        e.preventDefault();

        if (!isWarehouse) {
            if (!requestForm?.requesting_store || !requestForm?.source_store) {
                setMessage({
                    type: 'danger',
                    text: warehouseStore
                        ? 'Please wait for store context to load and try again.'
                        : 'Warehouse store could not be determined. Please contact an administrator.',
                });
                return;
            }
        }
        const payloadItems = items.map((it) => ({
            product: it.product,
            quantity: it.quantity,
            unit: it.unit || null,
            conversion_factor: it.conversion_factor,
        }));

        if (payloadItems.some((it) => !it.product || !it.quantity)) {
            setMessage({ type: 'danger', text: 'Please select product and quantity for all items.' });
            return;
        }

        try {
            if (editingRequestId) {
                await editRequest(editingRequestId, { ...requestForm, items: payloadItems });
                setMessage({ type: 'success', text: 'Transfer Request updated successfully!' });
            } else {
                await addRequest({...requestForm, items: payloadItems});
                setMessage({type: 'success', text: 'Transfer Request submitted successfully!'});
            }
            setShowRequestModal(false);
            setRequestPage(1);
            setEditingRequestId(null);
            resetItems();
        } catch (error) {
            setMessage({ type: 'danger',
                text: error?.response?.data?.detail || 'Failed to save transfer request.'
            });
        }
    };

    const submitTransfer = async (e) => {
        e.preventDefault();
        setTransferError('');
        const payloadItems = items.map((it) => ({
            product: it.product,
            quantity: it.quantity,
            unit: it.unit || null,
            conversion_factor: it.conversion_factor,
        }));

        if (payloadItems.some((it) => !it.product || !it.quantity)) {
            setTransferError('Please select product and quantity for all items.');
            return;
        }
        try {
            await addTransfer({ ...transferForm, items: payloadItems });
            setTransferPage(1);
            setMessage({ type: 'success', text: 'Transfer completed successfully!' });
            setShowTransferModal(false);
            resetItems();
        } catch (error) {
            const detail = error?.response?.data?.detail;
            setTransferError(
                Array.isArray(detail) ? detail[0] : detail || 'Failed to complete transfer.'
            );
        }
    };

    const productOptions = useMemo(
        () => (Array.isArray(allProducts) ? allProducts : []).map((p) => ({
            value: p.id,
            label: p.product_name,
        })),
        [allProducts]
    );

    const openCreateRequestModal = () => {
        setEditingRequestId(null);
        resetItems();
        setRequestForm({
            requesting_store: currentStore?.id || "",
            source_store: !isWarehouse && warehouseStore?.id ? warehouseStore.id : "",
            remarks: "",
        });
        setShowRequestModal(true);
    };

    const openEditRequestModal = (requestRecord) => {
        setEditingRequestId(requestRecord.id);
        setRequestForm({
            requesting_store: requestRecord.requesting_store || "",
            source_store: requestRecord.source_store || "",
            remarks: requestRecord.remarks || "",
        });
        const preparedItems = (requestRecord.items || []).map((item) => {
            const product = (Array.isArray(allProducts) ? allProducts : []).find(
                (p) => String(p.id) === String(item.product)
            );
            return {
                product: item.product,
                quantity: item.quantity,
                unit: item.unit || "",
                conversion_factor: item.conversion_factor || 1,
                availableUnits: buildAvailableUnits(product),
                sourceStock: null,
                sourceStockUnit: '',
                sourceStockLoading: true,
                sourceStockError: '',
            };
        });
        setItems(preparedItems);
        setShowRequestModal(true);

        preparedItems.forEach((preparedItem, index) => {
            fetchTransferRequestSourceStock(requestRecord.source_store, preparedItem.product)
                .then((stock) => {
                    setItems((current) => current.map((item, itemIndex) => (
                        itemIndex === index && String(item.product) === String(preparedItem.product)
                            ? {
                                ...item,
                                sourceStock: stock.available_qty,
                                sourceStockUnit: stock.base_unit_name || 'base units',
                                sourceStockLoading: false,
                            }
                            : item
                    )));
                })
                .catch((error) => {
                    setItems((current) => current.map((item, itemIndex) => (
                        itemIndex === index && String(item.product) === String(preparedItem.product)
                            ? {
                                ...item,
                                sourceStockLoading: false,
                                sourceStockError: error?.response?.data?.detail || 'Unable to load source stock.',
                            }
                            : item
                    )));
                });
        });
    };

    const applyTransferFilters = () => {
        setTransferPage(1);
        loadTransfers(transferFilters, 1);
    };

    const clearTransferFilters = () => {
        const cleared = { start_date: "", end_date: "", transfer_id: "", product: "", status: "" };
        setTransferFilters(cleared);
        setTransferPage(1);
        loadTransfers(cleared, 1);
    };

    const transferPages = Math.max(1, Math.ceil(transferCount / 20));
    const requestPages = Math.max(1, Math.ceil(requestCount / 20));
    const pageItems = (current, total, onChange) => (
        <Pagination className="justify-content-center mt-3">
            <Pagination.Prev disabled={current <= 1} onClick={() => onChange(current - 1)} />
            {Array.from({length: Math.min(5, total)}, (_, index) => {
                const start = Math.max(1, Math.min(current - 2, total - 4));
                const page = start + index;
                return <Pagination.Item key={page} active={page === current} onClick={() => onChange(page)}>{page}</Pagination.Item>;
            })}
            <Pagination.Next disabled={current >= total} onClick={() => onChange(current + 1)} />
        </Pagination>
    );

    return (
        <div className="wh-dashboard ops-page">
            <DashboardHero eyebrow="Inter-store operations" title="Stock transfers" description="Coordinate requests, move stock and track returns between stores." action="Inventory overview" to="/inventory" />
            <div className="back-button-container">
                <Button variant="danger" onClick={handleBack}>Back</Button>
            </div>
            <Row className="mb-4">
                <Col md={8}>
                    <h3 className="fw-bold mb-1">Transfer control centre</h3>
                    <p className="text-muted mb-0">
                        Move products between stores and approval flow
                    </p>
                </Col>
                <Col md={4} className="text-md-end mt-3 mt-md-0">
                    {!isWarehouse ? (
                        <Button
                            variant="outline-primary"
                            className="me-2"
                            onClick={() => {
                                openCreateRequestModal();
                            }}
                        >
                            Request Transfer
                        </Button>
                    ) : (
                        <Button
                            variant="primary"
                            onClick={() => {
                                resetItems();
                                setTransferError('');
                                setShowTransferModal(true);
                            }}
                        >
                            New Direct Transfer
                        </Button>
                    )}
                </Col>
            </Row>

            {message && (
                <Alert variant={message.type} onClose={() => setMessage(null)} dismissible>
                    {message.text}
                </Alert>
            )}

            <DashboardMetrics items={[
                {label:'Transfer requests', value:loading ? '…' : requestCount, hint:'All requests in your current scope', icon:'bi-inboxes'},
                {label:'Matching transfers', value:loading ? '…' : transferCount, hint:'Matches the current transfer filters', icon:'bi-arrow-left-right', tone:'blue'},
                {label:'Operating store', value:currentStore?.name || 'Select a store', hint:isWarehouse ? 'Warehouse · approve requests and transfer stock' : 'Outlet · request stock from the warehouse', icon:'bi-shop', tone:'amber'},
            ]} />

            <Card className="shadow-sm border-0">
                <Card.Body>
                    <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab}>
                        <Nav.Item>
                            <Nav.Link eventKey="requests">Transfer Requests</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="transfers">Transfers</Nav.Link>
                        </Nav.Item>
                    </Nav>

                    <div className="pt-4">
                        {loading && <Spinner animation="border" />}

                        {activeTab === 'requests' && (
                            <>
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Date</th>
                                        <th>Requesting Store</th>
                                        <th>Source Store</th>
                                        <th>Status</th>
                                        <th>Remarks</th>
                                        <th>View</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {requests.length ? requests.map((req) => (
                                        <tr key={req.id}>
                                            <td>{req.request_id || `#${req.id}`}</td>
                                            <td>{formatDateTime(req.request_date)}</td>
                                            <td>{getStoreName(req.requesting_store)}</td>
                                            <td>{getStoreName(req.source_store)}</td>
                                            <td>
                                                <Badge bg={req.status === 'APPROVED' ? 'success' : req.status === 'REJECTED' ? 'secondary' : 'warning'}>
                                                    {req.status}
                                                </Badge>
                                            </td>
                                            <td>{req.remarks || "-"}</td>
                                            <td>
                                                <Button
                                                    variant="info"
                                                    size="sm"
                                                    onClick={() => openViewModal('request', req)}
                                                    title="View request items"
                                                >
                                                    <FontAwesomeIcon icon={faEye} />
                                                </Button>
                                            </td>
                                            <td>
                                                {isWarehouse && isAdmin && req.status === 'PENDING' ? (
                                                    <>
                                                        <Button
                                                            size="sm" variant="outline-secondary" className="me-2"
                                                            onClick={() => openEditRequestModal(req)}
                                                            title="Adjust request before approval"
                                                        >
                                                            <FontAwesomeIcon icon={faPen} />
                                                        </Button>
                                                        <Button
                                                            size="sm" variant="success" className="me-2"
                                                            onClick={() => handleApprove(req.id)}
                                                        >
                                                            Approve
                                                        </Button>
                                                        <Button
                                                            size="sm" variant="outline-danger"
                                                            onClick={() => handleReject(req.id)}
                                                        >
                                                            Reject
                                                        </Button>
                                                    </>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="8" className="text-center text-muted">
                                                No transfer requests available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                            {requestPages > 1 && pageItems(requestPage, requestPages, (page) => {
                                setRequestPage(page);
                                loadRequests(page);
                            })}
                            </>
                        )}

                        {activeTab === 'transfers' && (
                            <>
                            <Row className="g-3 align-items-end mb-3">
                                <Col md={2}>
                                    <Form.Label>From Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={transferFilters.start_date}
                                        onChange={(e) => setTransferFilters({...transferFilters, start_date: e.target.value})}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Label>To Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={transferFilters.end_date}
                                        onChange={(e) => setTransferFilters({...transferFilters, end_date: e.target.value})}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Label>Transfer ID</Form.Label>
                                    <Form.Control
                                        value={transferFilters.transfer_id}
                                        onChange={(e) => setTransferFilters({...transferFilters, transfer_id: e.target.value})}
                                        placeholder="2026-TR-000001"
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Label>Product</Form.Label>
                                    <Select
                                        value={productOptions.find((p) => String(p.value) === String(transferFilters.product)) || null}
                                        options={productOptions}
                                        onChange={(selectedOption) =>
                                            setTransferFilters({...transferFilters, product: selectedOption ? selectedOption.value : ''})}
                                        isClearable
                                        isSearchable
                                        placeholder="Filter by product..."
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        value={transferFilters.status}
                                        onChange={(e) => setTransferFilters({...transferFilters, status: e.target.value})}
                                    >
                                        <option value="">All</option>
                                        <option value="IN_TRANSIT">In transit · awaiting receipt</option>
                                        <option value="COMPLETED">Completed</option>
                                        <option value="PARTIALLY_RETURNED">Partially Returned</option>
                                        <option value="RETURNED">Returned</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Button variant="primary" className="me-2" onClick={applyTransferFilters}>Filter</Button>
                                    <Button variant="outline-secondary" onClick={clearTransferFilters}>Clear</Button>
                                </Col>
                            </Row>

                                <Table responsive hover>
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Date</th>
                                            <th>From</th>
                                            <th>To</th>
                                            <th>Status</th>
                                            <th>Remarks</th>
                                            <th>View</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transfers.length ? transfers.map((transfer) => (
                                            <tr key={transfer.id}>
                                                <td>{transfer.transfer_id || `TR-${transfer.id}`}</td>
                                                <td>{formatDateTime(transfer.transfer_date)}</td>
                                                <td>{getStoreName(transfer.from_store)}</td>
                                                <td>{getStoreName(transfer.to_store)}</td>
                                                <td>
                                                    <Badge bg={transfer.status === 'IN_TRANSIT' ? 'info' : transfer.status === 'RETURNED' ? 'secondary' : transfer.status === 'PARTIALLY_RETURNED' ? 'warning' : 'success'}>
                                                        {transfer.status || 'COMPLETED'}
                                                    </Badge>
                                                </td>
                                                <td><div>{transfer.remarks || "-"}</div>
                                                    <small className="d-block text-muted">Approved / dispatched by: {transfer.created_by_name || 'Not recorded'}</small>
                                                    <small className="d-block text-muted">Received by: {transfer.received_by_name || (transfer.status === 'IN_TRANSIT' ? 'Awaiting receipt' : 'Legacy receipt · not recorded')}{transfer.received_at ? ` · ${formatDateTime(transfer.received_at)}` : ''}</small>
                                                    {transfer.receipt_issue && <div className="text-danger small mt-1" style={{whiteSpace:'pre-wrap'}}>Delivery issue: {transfer.receipt_issue}</div>}
                                                </td>
                                                <td>
                                                    <Button
                                                        variant="info"
                                                        size="sm"
                                                        onClick={() => openViewModal('transfer', transfer)}
                                                        title="View transfer items"
                                                    >
                                                        <FontAwesomeIcon icon={faEye} />
                                                    </Button>
                                                </td>
                                                <td>
                                                    {transfer.status === 'IN_TRANSIT' && String(transfer.to_store) === String(currentStore?.id) && <Button size="sm" onClick={() => openReceive(transfer)}>Receive products</Button>}
                                                    {canReturn && ['COMPLETED', 'PARTIALLY_RETURNED'].includes(transfer.status) && <Button variant="outline-warning" size="sm" onClick={() => openReturnModal(transfer)} title="Return transfer"><FontAwesomeIcon icon={faRotateLeft} /> Return</Button>}
                                                    {isAdmin && ['IN_TRANSIT', 'COMPLETED', 'PARTIALLY_RETURNED', 'RETURNED'].includes(transfer.status) ? (
                                                        <>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="me-2"
                                                                onClick={() => handlePrintTransfer(transfer)}
                                                                title="Reprint transfer receipt"
                                                            >
                                                                <FontAwesomeIcon icon={faPrint} />
                                                            </Button>
                                                        </>
                                                    ) : "-"}
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="8" className="text-center text-muted">
                                                    No transfers available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                                {transferPages > 1 && pageItems(transferPage, transferPages, (page) => {
                                    setTransferPage(page);
                                    loadTransfers(transferFilters, page);
                                })}
                                </>
                        )}
                    </div>
                </Card.Body>
            </Card>

            <ViewItemsModal
                show={showViewModal}
                onHide={() => {
                    setShowViewModal(false);
                    setViewModalType(null);
                    setViewRecord(null);
                }}
                type={viewModalType}
                record={viewRecord}
                getProductName={getProductName}
                getUnitLabel={getUnitLabel}
                stockPreview={stockPreview}
            />

            <TransferModal
                show={showRequestModal}
                onHide={closeRequestModal}
                title={editingRequestId ? "Adjust Transfer Request" : "Create Transfer Request"}
                handleSubmit={submitRequest}
                items={items}
                productOptions={productOptions}
                stores={stores}
                currentStore={currentStore}
                warehouseStore={warehouseStore}
                isWarehouse={isWarehouse}
                form={requestForm}
                setForm={setRequestForm}
                handleItemChange={handleItemChange}
                handleProductSelect={handleProductSelect}
                handleUnitSelect={handleUnitSelect}
                addLine={addLine}
                removeLine={removeLine}
                mode="request"
                submitLabel={editingRequestId ? "Save Request" : "Save"}
            />

            <TransferModal
                show={showTransferModal}
                onHide={() => {
                    setShowTransferModal(false);
                    setTransferError('');
                }}
                title="Create Direct Transfer"
                handleSubmit={submitTransfer}
                items={items}
                productOptions={productOptions}
                stores={stores}
                currentStore={currentStore}
                warehouseStore={warehouseStore}
                isWarehouse={isWarehouse}
                form={transferForm}
                setForm={setTransferForm}
                handleItemChange={handleItemChange}
                handleProductSelect={handleProductSelect}
                handleUnitSelect={handleUnitSelect}
                addLine={addLine}
                removeLine={removeLine}
                mode="transfer"
                error={transferError}
            />

            <Modal show={Boolean(receiving)} onHide={() => !receiptBusy && setReceiving(null)} size="lg" centered>
                <Modal.Header closeButton={!receiptBusy}><Modal.Title>Receive {receiving?.transfer_id}</Modal.Title></Modal.Header>
                <Modal.Body>
                    {receiptError && <Alert variant="danger">{receiptError}</Alert>}
                    <p>From {getStoreName(receiving?.from_store)} to {getStoreName(receiving?.to_store)}. Check quantities and condition against the delivery.</p>
                    <Table responsive><thead><tr><th>Product</th><th>Dispatched quantity</th><th>Unit</th></tr></thead><tbody>{receiving?.items?.map(item => <tr key={item.id}><td>{getProductName(item.product)}</td><td>{item.quantity}</td><td>{getUnitLabel(item.product, item.unit)}</td></tr>)}</tbody></Table>
                    <Form.Group controlId="transfer-receipt-issue"><Form.Label>Delivery issue · optional</Form.Label><Form.Control as="textarea" rows={4} maxLength={5000} value={receiptIssue} onChange={e => setReceiptIssue(e.target.value)} placeholder="Describe missing items, damage, quantities affected or other concerns…" /><Form.Text>Saved on this transfer and sent privately to the dispatcher/approver and active admin accounts when you receive.</Form.Text></Form.Group>
                    <Alert variant="warning" className="mt-3">Receiving posts every dispatched quantity, even if you report missing or damaged goods. The warehouse must resolve discrepancies separately. Only Admin or Manager users can process returns.</Alert>
                    <Form.Check id="confirm-full-transfer" label="I confirm receipt will add all listed quantities to this branch." checked={receiptConfirmed} onChange={e => setReceiptConfirmed(e.target.checked)} />
                </Modal.Body><Modal.Footer><Button variant="outline-secondary" disabled={receiptBusy} onClick={() => setReceiving(null)}>Cancel</Button><Button disabled={!receiptConfirmed || receiptBusy} onClick={confirmReceipt}>{receiptBusy ? 'Receiving…' : 'Confirm full receipt'}</Button></Modal.Footer>
            </Modal>

            <ReturnTransferModal
                show={showReturnModal}
                onHide={closeReturnModal}
                transfer={returnTransferRecord}
                returnItems={returnItems}
                getProductName={getProductName}
                getUnitLabel={getUnitLabel}
                getReturnableQuantity={getReturnableQuantity}
                onQuantityChange={handleReturnQuantityChange}
                onReturnAll={fillAllReturnQuantities}
                onSubmit={submitReturnItems}
            />
        </div>
    );
}

function ReturnTransferModal({
    show,
    onHide,
    transfer,
    returnItems,
    getProductName,
    getUnitLabel,
    getReturnableQuantity,
    onQuantityChange,
    onReturnAll,
    onSubmit,
}) {
    const items = Array.isArray(transfer?.items) ? transfer.items : [];
    const valueByItem = new Map(returnItems.map((row) => [String(row.item), row.quantity]));

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Return Transfer Items</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <strong>Transfer:</strong> {transfer?.transfer_id || (transfer?.id ? `TR-${transfer.id}` : '-')}
                </div>
                <Table responsive bordered hover>
                    <thead>
                    <tr>
                        <th>Product</th>
                        <th>Unit</th>
                        <th>Transferred</th>
                        <th>Returned</th>
                        <th>Remaining</th>
                        <th>Return Qty</th>
                    </tr>
                    </thead>
                    <tbody>
                    {items.length ? items.map((item) => {
                        const remaining = getReturnableQuantity(item);
                        return (
                            <tr key={item.id}>
                                <td>{getProductName(item.product)}</td>
                                <td>{getUnitLabel(item.product, item.unit)}</td>
                                <td>{item.quantity}</td>
                                <td>{item.returned_quantity || 0}</td>
                                <td>{remaining}</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        max={remaining}
                                        step="0.01"
                                        disabled={remaining <= 0}
                                        value={valueByItem.get(String(item.id)) || ""}
                                        onChange={(event) => onQuantityChange(item.id, event.target.value)}
                                    />
                                </td>
                            </tr>
                        );
                    }) : (
                        <tr>
                            <td colSpan="6" className="text-center text-muted">No items available</td>
                        </tr>
                    )}
                    </tbody>
                </Table>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-warning" onClick={onReturnAll}>Return All Remaining</Button>
                <Button variant="secondary" onClick={onHide}>Cancel</Button>
                <Button variant="warning" onClick={onSubmit}>Process Return</Button>
            </Modal.Footer>
        </Modal>
    );
}

function ViewItemsModal({ show, onHide, type, record, getProductName, getUnitLabel, stockPreview}) {
    const items = Array.isArray(record?.items) ? record.items : [];
    const isTransfer = type === 'transfer';

    const headerId = type === 'transfer'
        ? (record?.transfer_id || (record?.id != null ? `TR-${record.id}` : '-'))
        : (record?.request_id || (record?.id != null ? `#${record.id}` : '-'));

    const title = type === 'transfer' ? 'Transfer Details' : 'Transfer Request Details';

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <div><strong>ID:</strong> {headerId}</div>
                    <div><strong>Remarks:</strong> {record?.remarks || '-'}</div>
                    {type === 'transfer' && <><div><strong>Approved / dispatched by:</strong> {record?.created_by_name || 'Not recorded'}</div><div><strong>Received by:</strong> {record?.received_by_name || 'Not recorded'}</div>{record?.receipt_issue && <Alert variant="warning" style={{whiteSpace:'pre-wrap'}}>Delivery issue: {record.receipt_issue}</Alert>}</>}
                </div>

                <Table responsive bordered hover>
                    <thead>
                        <tr>
                            <th style={{ width: isTransfer ? '35%' : '45%' }}>Product</th>
                            <th style={{ width: '15%' }}>Unit</th>
                            <th style={{ width: '15%' }}>{isTransfer ? 'Transferred' : 'Requested'}</th>
                            {isTransfer ? (
                                <>
                                    <th style={{ width: '15%' }}>Returned</th>
                                    <th style={{ width: '15%' }}>Remaining</th>
                                    <th style={{ width: '10%' }}>Status</th>
                                </>
                            ) : (
                                <>
                                    <th style={{ width: '10%' }}>Available</th>
                                    <th style={{ width: '10%' }}>Status</th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                    {items.length ? items.map((it, idx) => {
                        const preview = stockPreview.find(p => String(p.product_id) === String(it.product));
                        const quantity = Number(it.quantity || 0);
                        const returned = Number(it.returned_quantity || 0);
                        const remaining = Math.max(0, quantity - returned);

                        return (
                            <tr key={idx}>
                                <td>{getProductName(it.product)}</td>
                                <td>{getUnitLabel(it.product, it.unit)}</td>
                                <td>{it.quantity}</td>
                                {isTransfer ? (
                                    <>
                                        <td>{returned}</td>
                                        <td>{remaining}</td>
                                        <td>
                                            {returned > 0 ? (
                                                <Badge bg={remaining > 0 ? 'warning' : 'secondary'}>
                                                    {remaining > 0 ? 'Partially returned' : 'Returned'}
                                                </Badge>
                                            ) : "-"}
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td>{preview?.available_qty ?? '-'}</td>
                                        <td>
                                            {preview ? (
                                                <Badge bg={preview.status === "ok" ? "success" : "danger"}>
                                                    {preview.status}
                                                </Badge>
                                            ) : "-"}
                                        </td>
                                    </>
                                )}
                            </tr>
                        );
                    }) : (
                    <tr>
                        <td colSpan="5" className="text-center text-muted">
                            No items available
                        </td>
                    </tr>
                    )}
                    </tbody>
                </Table>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}

function TransferModal({
       show,
       onHide,
       title,
       handleSubmit,
       items,
       productOptions,
       stores,
       currentStore,
       warehouseStore,
       isWarehouse,
       form,
       setForm,
       handleItemChange,
       handleProductSelect,
       handleUnitSelect,
       addLine,
       removeLine,
       mode,
       error,
       submitLabel = "Transfer"
    }) {

    const lockRequestStores = mode === 'request' && !isWarehouse;

    const requestingStoreOptions = lockRequestStores
        ? (currentStore ? [currentStore] : [])
        : stores;
    const sourceStoreOptions = lockRequestStores
        ? (warehouseStore ? [warehouseStore] : [])
        : stores;

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <Row className="g-3 mb-3">
                        <Col md={6}>
                            <Form.Label>{mode === "request" ? "Requesting Store" : "From Store"}</Form.Label>
                                <Form.Select
                                    value={mode === "request" ? form.requesting_store : form.from_store}
                                    onChange={(e) =>
                                        setForm({...form, [mode === "request" ? "requesting_store" : "from_store"]: e.target.value})
                                    }
                                    required
                                    disabled={lockRequestStores && mode === 'request'}
                                >
                                    <option value="">Select Store</option>
                                    {requestingStoreOptions.map((store) => (
                                        <option key={store.id} value={store.id}>
                                            {store.name}
                                        </option>
                                    ))}
                                </Form.Select>
                        </Col>

                        <Col md={6}>
                            <Form.Label>{mode === "request" ? "Source Store" : "To Store"}</Form.Label>
                            <Form.Select
                                value={mode === "request" ? form.source_store : form.to_store}
                                onChange={(e) =>
                                    setForm({...form, [mode === "request" ? "source_store" : "to_store"]: e.target.value})
                                }
                                required
                                disabled={lockRequestStores && mode === 'request'}
                            >
                                <option value="">Select Store</option>
                                {sourceStoreOptions.map((store) => (
                                    <option key={store.id} value={store.id}>
                                        {store.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>

                    {items.map((item, index) => (
                        <Card key={index} className="mb-3 border-0 shadow-sm">
                            <Card.Body>
                                <Row className="g-3 align-items-end">
                                    <Col md={6}>
                                        <Form.Label>Product</Form.Label>
                                        <Select
                                            value={productOptions.find((p) => String(p.value) === String(item.product))}
                                            options={productOptions}
                                            onChange={(selectedOption) =>
                                                handleProductSelect(index, selectedOption ? selectedOption.value : '')}
                                            isSearchable={true}
                                            placeholder="Select Product"
                                        />
                                        {mode === 'request' && item.product && (
                                            <div className="small mt-1">
                                                {item.sourceStockLoading ? (
                                                    <span className="text-muted">Checking source stock...</span>
                                                ) : item.sourceStockError ? (
                                                    <span className="text-danger">{item.sourceStockError}</span>
                                                ) : item.sourceStock !== null && item.sourceStock !== undefined ? (
                                                    <span className="text-success">
                                                        Available at source: {Number(item.sourceStock).toLocaleString()} {item.sourceStockUnit || 'base units'}
                                                    </span>
                                                ) : null}
                                            </div>
                                        )}
                                    </Col>

                                    <Col md={3}>
                                        <Form.Label>Quantity</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            value={item.quantity}
                                            onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                                            required
                                        />
                                    </Col>

                                    {Array.isArray(item.availableUnits) && item.availableUnits.length > 1 ? (
                                        <Col md={2}>
                                            <Form.Label>Unit</Form.Label>
                                            <Form.Select
                                                value={item.unit}
                                                onChange={(e) => handleUnitSelect(index, e.target.value)}
                                                required
                                            >
                                                <option value="">Select unit</option>
                                                {item.availableUnits.map((u) => (
                                                    <option key={u.id} value={u.id}>
                                                        {u.name}{u.abbreviation ? ` (${u.abbreviation})` : ''}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                    ) : (
                                        <Col md={2}>
                                            <Form.Label>Unit</Form.Label>
                                            <Form.Control
                                                value={
                                                    item.availableUnits?.[0]
                                                        ? `${item.availableUnits[0].name}${item.availableUnits[0].abbreviation ? ` (${item.availableUnits[0].abbreviation})` : ''}`
                                                        : ''
                                                }
                                                placeholder="-"
                                                disabled
                                                readOnly
                                            />
                                        </Col>
                                    )}

                                    <Col md={1}>
                                        {index > 0 && (
                                            <Button
                                                variant="outline-danger"
                                                className="w-100"
                                                onClick={() => removeLine(index)}
                                                title="Remove item"
                                                aria-label="Remove item"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </Button>
                                            )}
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    ))}

                    <Button variant="outline-secondary" onClick={addLine}>+ Add Item</Button>
                    <Form.Group className="mt-3">
                        <Form.Label>Remarks</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={form.remarks}
                            onChange={(e) => setForm({...form, remarks: e.target.value})}
                        />
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="light" onClick={onHide}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="primary">{submitLabel}</Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
